import React, {useContext, useMemo, useState} from 'react';

import {Table, TableInterface} from '@veneer/core';

import {GlobalTranslationsContext} from '../../i18n';
import {PaginatedRequest} from '../../util';

import {useTable} from './hooks';
import {DataTableProps, FilterValue, InitialPreferences, UseColumnsHook, UseMapDataHook} from './types';

const DataTable = <F extends Partial<Record<C, FilterValue>>, O extends string, T, C extends string>({
    fetch,
    loadFilters,
    useMapData,
    useColumns,
    initialPreferences,
    defaultFilters,
    ...props
}: {
    fetch: PaginatedRequest<F, O, T>;
    loadFilters: (defaults?: F) => F;
    useMapData: UseMapDataHook<T, C>;
    useColumns: UseColumnsHook<F, C, O>;
    initialPreferences?: InitialPreferences<O>;
    defaultFilters?: F;
} & Omit<TableInterface, keyof DataTableProps | 'data' | 'columns' | 'i18n'>): JSX.Element => {
    const [filters, setFilters] = useState<F>(() => loadFilters(defaultFilters));
    const {data: rawData, table} = useTable(fetch, filters, initialPreferences);
    const {veneer: v} = useContext(GlobalTranslationsContext);

    const mapData = useMapData(table);

    const data = useMemo(() => rawData.map(mapData), [rawData, mapData]);
    const columns = useColumns(filters, setFilters);

    return (
        <Table
            data={data}
            columns={columns}
            i18n={v.table}
            {...table}
            {...props}
        />
    );
};

export default DataTable;
